<template>
  <div>
    <create-document :visible.sync="visibleCreateDocumentModal" :associate_id="associate.id" @add="onCreateDocument" />

    <div class="flex flex-column gap-3">
      <div class="flex justify-content-between align-items-start">
        <h3 class="m-0">Attached Documents</h3>
        <div class="flex flex-column gap-2">
          <div class="flex gap-2 justify-content-end">
            <a-button icon="plus" type="primary" @click="openCreateModal"> Create </a-button>
            <template v-if="documents.length">
              <a-button icon="export" type="primary" @click="$emit('export')">Export</a-button>
            </template>
          </div>
        </div>
      </div>

      <a-table
        size="small"
        :style="{ whiteSpace: 'pre' }"
        :columns="columns"
        :data-source="documents"
        :loading="loading"
        :pagination="pagination"
        :rowKey="record => record.id"
        :locale="{
          emptyText: searchValue ? getEmptyText() : 'No Attached Documents'
        }"
        :rowClassName="getRowClassName"
        :customRow="
          record => ({
            on: {
              click: () => handleAttachedView(record)
            }
          })
        "
        @change="handleTableChange"
        :scroll="{ x: 900, y: 380 }"
      >
        <span slot="name" slot-scope="text, record">
          {{ record.name }}
        </span>
        <span slot="added_by" slot-scope="text, record">
          {{ getAddedBy(record) }}
        </span>
        <span slot="date" slot-scope="text, record">
          {{ record.created_at ? formatDateString(record.created_at) : '-' }}
        </span>
        <span slot="expired_at" slot-scope="text, record">
          {{ record.expiration_date ? formatDateString(record.expiration_date) : '-' }}
        </span>
        <span slot="status" slot-scope="text, record">
          <a-tag class="m-0" :color="getAssociateDocumentStatus(record.status).color">
            {{ getAssociateDocumentStatus(record.status).title }}</a-tag
          >
        </span>
        <span slot="actions" slot-scope="text, record">
          <span :style="{ display: 'none' }">
            <export-button
              :ref="`exportRef_${record.id}`"
              :only-icon="true"
              :export-payload="exportDocumentPayload(record.id)"
              @updateUrl="url => afterDownloadDocument(url, record.id)"
          /></span>
          <button-more :record="record" :menu-items="menuItems" />
        </span>
      </a-table>
    </div>
  </div>
</template>

<script>
import { Button, notification, Table, Tag } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import ProfileCreateDocument from '../../document/components/attached/attached-create.vue'
import ExportButton from '@/components/export-button.vue'
import { h } from 'vue'
import tableMixin from '@/mixins/tableMixin'
import BulkArchiveMixin from '../mixins/BulkArchiveMixin'

const { mapState: profileState, mapActions: profileActions } = createNamespacedHelpers('profile')
const {
  mapActions: documentActions,
  mapState: documentState,
  mapGetters: documentGetters
} = createNamespacedHelpers('document')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-table': Table,
    'a-button': Button,
    'a-tag': Tag,
    'create-document': ProfileCreateDocument,
    'export-button': ExportButton
  },
  props: {
    searchValue: {
      type: String,
      default: undefined
    }
  },
  mixins: [tableMixin, BulkArchiveMixin],
  data() {
    return {
      visibleCreateDocumentModal: false,

      sendDocumentId: undefined,

      pagination: {
        showSizeChanger: true,
        pageSizeOptions: ['5', '10', '20', '50'],
        pageSize: 50,
        total: 0,
        current: 1
      },

      documents: [],

      loading: false,
      loadingButtons: {},

      columns: [
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          width: '300px',
          scopedSlots: { customRender: 'name' },
          sorter: (a, b) => (a.name == null ? -1 : b.name == null ? 1 : a.name.localeCompare(b.name))
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          scopedSlots: { customRender: 'status' },
          sorter: (a, b) => (a.status == null ? -1 : b.status == null ? 1 : a.status.localeCompare(b.status))
        },
        {
          title: 'Added by',
          dataIndex: 'added_by',
          key: 'added_by',
          scopedSlots: { customRender: 'added_by' },
          sorter: (a, b) => this.getAddedBy(a).localeCompare(this.getAddedBy(b))
        },
        {
          title: 'Created At',
          dataIndex: 'date',
          key: 'date',
          scopedSlots: { customRender: 'date' },
          sorter: (a, b) => {
            const dateA = a.created_at ? new Date(a.created_at) : null
            const dateB = b.created_at ? new Date(b.created_at) : null

            if (!dateA && !dateB) return 0
            if (!dateA) return 1
            if (!dateB) return -1

            return dateA - dateB
          }
        },
        {
          title: 'Expired At',
          dataIndex: 'expired_at',
          key: 'expired_at',
          scopedSlots: { customRender: 'expired_at' },
          sorter: (a, b) => {
            const expirationDateA = a.expiration_date ? new Date(a.expiration_date) : null
            const expirationDateB = b.expiration_date ? new Date(b.expiration_date) : null

            if (!expirationDateA && !expirationDateB) return 0
            if (!expirationDateA) return 1
            if (!expirationDateB) return -1

            return expirationDateA - expirationDateB
          }
        },
        {
          title: 'Actions',
          dataIndex: 'actions',
          key: 'actions',
          scopedSlots: { customRender: 'actions' }
        }
      ]
    }
  },
  computed: {
    ...documentState({
      associateDocuments: state => state.associateDocuments
    }),
    ...documentGetters(['getAssociateDocumentStatus']),
    ...profileState({
      associate: state => state.associate,
      compliance: state => state.associateCompliance,
      certification: state => state.associateCertification
    }),

    menuItems() {
      return [
        {
          key: 'open',
          icon: 'fluent:open-20-regular',
          label: 'Open',
          visible: true,
          handler: record => {
            this.handleAttachedView(record)
          }
        },
        {
          key: 'archive',
          icon: 'carbon:archive',
          label: 'Delete',
          visible: true,
          handler: record => {
            this.deleteDocument(record.id)
          }
        },
        {
          key: 'bulk-archive-signed',
          icon: 'carbon:archive',
          label: 'Archive Signed Documents',
          visible: true,
          handler: record => {
            const templateId = record.template_id ?? record.template?.id
            if (!templateId) return
            this.openBulkArchiveConfirm({
              type: 'associate_document',
              object_id: templateId,
              name: record.name,
              onDone: () => this.fetchDocuments()
            })
          }
        },
        {
          key: 'export',
          icon: 'mdi:export',
          label: 'Export',
          visible: true,
          children: [
            {
              key: 'send-export-document',
              icon: 'carbon:email',
              label: 'Send to Email',
              visible: true,
              handler: record => {
                const refName = `exportRef_${record.id}`

                if (this.$refs[refName]) {
                  this.$refs[refName].openSendModal()
                }
              }
            },
            {
              key: 'download-export-document',
              icon: 'carbon:document-download',
              label: 'Download',
              visible: true,
              handler: record => {
                if (record.export_file) {
                  window.open(record.export_file.url, '_blank')
                } else {
                  const refName = `exportRef_${record.id}`

                  this.$refs[refName].download()
                }
              }
            }
          ]
        }
      ]
    }
  },
  watch: {
    associateDocuments: {
      handler(newVal) {
        this.pagination.total = newVal?.total_count ?? 0
        this.documents = newVal.items ?? []
      },
      immediate: true,
      deep: true
    },
    searchValue: {
      handler() {
        this.onSearch()
      }
    }
  },
  methods: {
    ...profileActions(['setAssociate', 'setAssociateCompliance', 'setAssociateCertification']),
    ...modalActions(['openModal']),
    ...documentActions(['getAssociateDocuments', 'exportDocument', 'deleteAssociateDocument', 'setAssociateDocuments']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    afterDownloadDocument(url, id) {
      const updatedDocuments = {
        ...this.associateDocuments,
        items: this.associateDocuments.items.map(item => {
          if (item.id === id) {
            return { ...item, export_file: { url } }
          } else {
            return item
          }
        })
      }

      this.setAssociateDocuments(updatedDocuments)
    },

    getEmptyText() {
      return h('span', [
        'Nothing matched your search. Please take a look at ',
        h(
          'span',
          {
            on: {
              click: () => {
                this.$emit('click', 'company')
              }
            },
            style: { color: '#2563eb', cursor: 'pointer' }
          },
          'Documents'
        ),
        ' — maybe you’ll find it there!'
      ])
    },

    formatDateString(dateString) {
      return moment(dateString).utc().format('MM/DD/YYYY')
    },

    getAddedBy(record) {
      const author = record?.author

      if (!author) {
        return '-'
      }

      if (author.id === this.associate.id || author.associate_number === this.associate.associate_number) {
        return 'Employee'
      }

      return [author.first_name, author.last_name].filter(Boolean).join(' ') || author.associate_number || '-'
    },

    onSearch() {
      this.pagination.current = 1

      this.fetchDocuments()
    },

    handleAttachedView(initial_document) {
      const updateDocumentInStore = (updatedDocument, isRenew = false) => {
        const documentId = isRenew ? updatedDocument.parent_id : updatedDocument.id
        const templateId = updatedDocument.template_id

        const updatedDocuments = {
          ...this.associateDocuments,
          items: isRenew
            ? this.associateDocuments.items.filter(item => item.id !== documentId)
            : this.associateDocuments.items.map(doc => (doc.id === documentId ? updatedDocument : doc))
        }

        const updateRequiredDocs = requiredDocs => {
          return requiredDocs.map(item => {
            if (item?.template?.id === templateId) {
              return { ...item, document: { ...updatedDocument } }
            } else if (item?.document?.id === documentId && item?.template?.id !== templateId) {
              return { ...item, document: null }
            }
            return item
          })
        }

        const updatedCompliance = {
          ...this.compliance,
          required_associate_docs: updateRequiredDocs(this.compliance.required_associate_docs)
        }

        const updatedCertification = {
          ...this.certification,
          required_associate_docs: updateRequiredDocs(this.certification?.required_associate_docs)
        }

        this.setAssociateCompliance(updatedCompliance)
        this.setAssociateCertification(updatedCertification)
        this.setAssociateDocuments(updatedDocuments)
      }

      this.openModal({
        type: 'attachedView',
        data: {
          visible: true,
          initial_document,
          onRenew: updatedDocument => updateDocumentInStore(updatedDocument, true),
          onUpdate: updatedDocument => updateDocumentInStore(updatedDocument, false)
        }
      })
    },

    onCreateDocument(document) {
      const updateRequiredDocs = requiredDocs => {
        return (requiredDocs ?? []).map(item => {
          if (item?.template?.id === document.templateId) {
            return { ...item, document: { ...document } }
          }
          return item
        })
      }

      const updatedCompliance = {
        ...this.compliance,
        required_associate_docs: updateRequiredDocs(this.compliance.required_associate_docs)
      }

      const updatedCertification = {
        ...this.certification,
        required_associate_docs: updateRequiredDocs(this.certification?.required_associate_docs)
      }

      this.setAssociateCompliance(updatedCompliance)
      this.setAssociateCertification(updatedCertification)
    },

    exportDocumentPayload(document_id) {
      return {
        object_ids: [document_id],
        type: 'associate_document'
      }
    },

    openCreateModal() {
      this.visibleCreateDocumentModal = true
    },

    isExpired(expiration_date) {
      return moment().utc().isSameOrAfter(moment(expiration_date).utc(), 'day')
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }

      this.fetchDocuments()
    },

    async fetchDocuments() {
      this.loading = true

      const payload = {
        associate_id: this.associate.id,
        page: this.pagination.current,
        size: this.pagination.pageSize,
        name: this.searchValue
      }

      try {
        await this.getAssociateDocuments(payload)
      } catch (error) {
        this.showNotification('error', 'Error', error || 'An error occurred')
      } finally {
        this.loading = false
      }
    },

    async deleteDocument(id) {
      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Are you sure?',
          description:
            'After deleting, the document will disappear from the profile and you will only be able to restore it through ePeople support. The person will need to re-upload it. Are you sure you want to delete the document?',
          onConfirm: async () => {
            await this.deleteAssociateDocument({
              document: id,
              associate_id: this.associate.id
            })

            this.showNotification('success', 'Success', 'Successfully deleted the document.')

            const removeDocument = requiredDocs => {
              return requiredDocs.map(item => {
                return item?.document?.id === id ? { ...item, document: null } : item
              })
            }

            const updatedCompliance = {
              ...this.compliance,
              required_associate_docs: removeDocument(this.compliance.required_associate_docs)
            }

            const updatedCertification = {
              ...this.certification,
              required_associate_docs: removeDocument(this.certification?.required_associate_docs)
            }

            this.setAssociateCompliance(updatedCompliance)
            this.setAssociateCertification(updatedCertification)

            this.fetchDocuments()
          }
        }
      })
    }
  },
  async mounted() {
    await this.fetchDocuments()
  }
}
</script>

<style scoped>
.list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 480px;
  overflow: auto;
}

.item {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid var(--border-color-base);
  border-radius: 4px;
}

::v-deep .ant-table-row-cell-break-word {
  text-wrap: initial;
}
</style>
